import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSignUp } from '@clerk/clerk-react';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { AuthCardLayout } from './layout/AuthCardLayout';
import { AuthErrorBanner } from './layout/AuthErrorBanner';
import { OAuthSocialButtons } from './forms/OAuthSocialButtons';
import { SignUpRegistrationForm } from './forms/SignUpRegistrationForm';
import { SignUpEmailVerificationForm } from './forms/SignUpEmailVerificationForm';

export const SignUpPage: React.FC = () => {
  const { isLoaded, signUp, setActive } = useSignUp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect_url') || '/';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [pendingVerification, setPendingVerification] = useState(false);
  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;
    setIsLoading(true);
    setError(null);

    try {
      await signUp.create({
        emailAddress: email.trim(),
        password,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
      });
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setPendingVerification(true);
      setResendCooldown(60);
    } catch (err: any) {
      setError(err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || 'Registration failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;
    setIsLoading(true);
    setError(null);

    try {
      const completeSignUp = await signUp.attemptEmailAddressVerification({ code: code.trim() });
      if (completeSignUp.status === 'complete') {
        await setActive({ session: completeSignUp.createdSessionId });
        navigate(redirectUrl, { replace: true });
      } else {
        setError(`Registration status: ${completeSignUp.status}. Additional steps may be required.`);
      }
    } catch (err: any) {
      setError(err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || 'Invalid verification code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!isLoaded || !signUp || resendCooldown > 0) return;
    setIsLoading(true);
    setError(null);
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setResendCooldown(60);
    } catch (err: any) {
      setError(err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || 'Failed to resend code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthSignUp = async (strategy: 'oauth_google') => {
    if (!isLoaded || !signUp) return;
    setOauthLoading(strategy);
    setError(null);
    try {
      await signUp.authenticateWithRedirect({ strategy, redirectUrl: '/sso-callback', redirectUrlComplete: redirectUrl });
    } catch (err: any) {
      setError(err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || 'OAuth registration failed.');
      setOauthLoading(null);
    }
  };

  return (
    <AuthCardLayout
      title={pendingVerification ? 'Verify Your Email' : 'Become a Conversora Partner'}
      subtitle={pendingVerification ? `Enter the 6-digit confirmation code sent to ${email}` : 'Earn 10%–20% lifetime recurring commissions on every referred merchant'}
      icon={pendingVerification ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Sparkles className="w-5 h-5" />}
      headerLinkPrompt="Already have an account?"
      headerLinkAction="Sign in"
      headerLinkHref="/sign-in"
    >
      <AuthErrorBanner error={error} />
      {!pendingVerification ? (
        <>
          <OAuthSocialButtons onSelect={handleOAuthSignUp} isLoading={!isLoaded || isLoading} oauthLoading={oauthLoading} />
          <SignUpRegistrationForm
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            isLoading={!isLoaded || isLoading}
            onSubmit={handleSignUpSubmit}
          />
        </>
      ) : (
        <SignUpEmailVerificationForm
          code={code}
          setCode={setCode}
          isLoading={!isLoaded || isLoading}
          resendCooldown={resendCooldown}
          onVerify={handleVerificationSubmit}
          onResend={handleResendCode}
        />
      )}
    </AuthCardLayout>
  );
};

export default SignUpPage;
